import { getRefresh, parseJwt, refreshAccessToken } from './auth.js';

// ─────────────────────────────────────────────────────────────────────────────
// Session lifecycle — the part of authentication the doctor experiences.
//
// The mechanics (PKCE, tokens, refresh) live in auth.js and are unchanged. What
// this module adds is the answer to a question auth.js never asked: what should
// happen to the WORK IN PROGRESS when a session ends?
//
// The old answer was `window.location.href = keycloak`. That is a full-document
// navigation: React unmounts, the DOM is destroyed, and everything not yet
// persisted goes with it. In this app that is a doctor's half-written SOAP
// note, a prescription being assembled, a differential they were reading — and
// it happened without warning, without confirmation, and (because the redirect
// lands on `/`) without returning them to the page they were on. A doctor who
// stepped out for fifteen minutes came back, typed one more line, and watched
// the visit disappear.
//
// So a session ending is now an EVENT, not a navigation:
//
//   expiring — the refresh token is close to its end. Raised early, while the
//              session can still be extended silently, because the best
//              handling of an expiry is for it never to become one.
//   expired  — it is over. The app STAYS MOUNTED and raises a dialog. Nothing
//              is destroyed, the doctor sees what they were doing, and the
//              re-login is something they choose rather than something that
//              happens to them.
//
// Timing is driven by the REFRESH token, not the access token. bffClient renews
// the access token silently whenever it is stale, so its expiry is invisible
// and warning on it would fire constantly for no reason. The refresh token's
// expiry is the moment the session actually dies.
// ─────────────────────────────────────────────────────────────────────────────

// The event BUS is the clinical module's (@parsina/framework/clinical): a 401
// raised inside a visit and one raised on پذیرش must reach the SAME listeners —
// the expiry dialogs below — and two copies of the bus would be two sets of
// listeners. Imported for this module's own watcher and re-exported so every
// existing import of it keeps working.
import { SessionEvent, onSessionEvent, emitSession, resetSessionListeners } from '@parsina/framework/clinical/src/lib/session.js';

export { SessionEvent, onSessionEvent, emitSession, resetSessionListeners };

// How long before the end to raise the warning. Long enough to finish a
// sentence and press a button; short enough that it is not asking about
// something ten minutes away, which trains people to dismiss it.
export const EXPIRY_WARNING_MS = 120_000;

// setTimeout is clamped to a 32-bit signed millisecond count, and a longer
// delay wraps to a negative value and fires IMMEDIATELY. A Keycloak SSO session
// can legitimately be days long, so a naive `setTimeout(warnAt)` would show the
// expiry warning the instant a doctor signed in.
const MAX_TIMEOUT_MS = 2_147_483_647;

// ── Returning the doctor to where they were ──────────────────────────────────
// Keycloak's redirect_uri is the origin, so a re-login lands on پذیرش unless
// something remembers the destination. That is a small loss on a list screen
// and a real one on /visit/:patientId — the doctor has to find the patient
// again and re-open the encounter, having just been interrupted.
//
// ── Why localStorage, and not the sessionStorage this used to be ────────────
// It was sessionStorage on the argument that an interrupted journey is a fact
// about ONE TAB. That reasoning holds for two tabs on a desktop and fails for
// every case a phone actually produces:
//
//   COLD-START DEEP LINK. A PWA shortcut, a shared /patients/:id, a /visit/:id
//   sent in a message. There is no session yet, so `sessionStorage` is empty by
//   definition — and the Keycloak round trip is a full document navigation, so
//   whatever we wrote before it is only readable afterwards if it outlived the
//   navigation. It did not: the doctor followed a link to a record and arrived
//   at پذیرش, every time.
//
//   THE PAYMENT RETURN. `location.href = gateway` leaves the app. From an
//   installed iOS PWA the gateway may open in a different browsing context, and
//   its redirect back to /credits?payment=… can land there with neither the
//   token nor the path. sessionStorage is per browsing context; localStorage is
//   per origin, so it survives exactly the hop that loses the other one.
//
// The tokens deliberately stay in sessionStorage. What moves is a NON-SECRET
// in-app path, so this changes the app's security posture not at all — which is
// why it was chosen over the other candidate fix (moving the tokens).
//
// The TTL is what keeps the cross-tab objection real but harmless: a remembered
// destination is only honoured for the length of a sign-in, and a stale one
// from an hour ago is ignored rather than teleporting a doctor into a screen
// they left long ago.

const RETURN_KEY = 'parsina_return_to';

/**
 * How long a remembered destination stays honourable.
 *
 * Long enough for a slow Keycloak round trip, an SMS code that takes a while to
 * arrive, or a payment at the gateway. Short enough that a path written before
 * lunch does not decide where the afternoon starts.
 */
export const RETURN_PATH_TTL_MS = 15 * 60_000;

/** Only in-app paths: an absolute URL here would be an open redirect. */
const inAppPath = (path) =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

export function rememberReturnPath(path = `${window.location.pathname}${window.location.search}`) {
  try {
    if (!inAppPath(path)) return;
    localStorage.setItem(RETURN_KEY, JSON.stringify({ path, ts: Date.now() }));
  } catch { /* private mode / storage disabled — returning home is an acceptable loss */ }
}

/** Read and clear the stored destination. One-shot by design. */
export function takeReturnPath({ now = () => Date.now() } = {}) {
  try {
    const raw = localStorage.getItem(RETURN_KEY);
    localStorage.removeItem(RETURN_KEY);
    if (!raw) return null;

    // A bare string is what the sessionStorage version wrote. Reading it keeps
    // the one doctor whose browser is mid-upgrade from losing their place.
    const stored = raw.startsWith('{') ? JSON.parse(raw) : { path: raw, ts: now() };
    if (!inAppPath(stored?.path)) return null;
    if (!Number.isFinite(stored?.ts) || now() - stored.ts > RETURN_PATH_TTL_MS) return null;
    return stored.path;
  } catch {
    return null;
  }
}

/**
 * Remember where the doctor is standing, before a `redirectToLogin()`.
 *
 * The one place that decides whether a path is worth remembering, so the four
 * boot branches that sign in cannot disagree: `/` is where a sign-in lands
 * anyway, so storing it is noise.
 */
export function rememberCurrentPathForLogin() {
  try {
    if (window.location.pathname === '/') return;
    rememberReturnPath(`${window.location.pathname}${window.location.search}`);
  } catch { /* nothing readable to remember */ }
}

// ── Expiry scheduling ────────────────────────────────────────────────────────

/** Epoch ms at which the current refresh token dies, or null if unknowable. */
export function refreshTokenExpiry() {
  const refresh = getRefresh();
  if (!refresh) return null;
  const payload = parseJwt(refresh);
  // Keycloak offline tokens carry exp: 0, meaning "does not expire". Treating
  // that as "expired in 1970" would raise the dialog immediately on login.
  if (!payload?.exp) return null;
  return payload.exp * 1000;
}

/**
 * Watch the current session and raise `expiring` then `expired` at the right
 * moments. Returns a cancel function.
 *
 * Re-arms itself after a successful silent renewal rather than assuming one
 * schedule for the session's whole life: every refresh mints a new token with a
 * new expiry, so a single set of timers would go off against a token that no
 * longer exists.
 */
export function watchSession({ emit = emitSession, now = () => Date.now() } = {}) {
  let warnTimer = null;
  let endTimer = null;
  let cancelled = false;

  const clear = () => {
    clearTimeout(warnTimer);
    clearTimeout(endTimer);
    warnTimer = null;
    endTimer = null;
  };

  const arm = () => {
    clear();
    if (cancelled) return;

    const expiry = refreshTokenExpiry();
    // No refresh token, or one that never expires: nothing to schedule. An
    // actual rejection still arrives through `emitSession` from bffClient, so
    // this is not the only path to the dialog.
    if (!expiry) return;

    const remaining = expiry - now();

    if (remaining <= 0) { emit(SessionEvent.EXPIRED); return; }

    const untilWarning = remaining - EXPIRY_WARNING_MS;
    if (untilWarning > 0) {
      warnTimer = setTimeout(() => emit(SessionEvent.EXPIRING), Math.min(untilWarning, MAX_TIMEOUT_MS));
    } else {
      emit(SessionEvent.EXPIRING);
    }
    endTimer = setTimeout(() => emit(SessionEvent.EXPIRED), Math.min(remaining, MAX_TIMEOUT_MS));
  };

  arm();

  return {
    /** Re-read the token and re-arm. Call after any successful refresh. */
    rearm: arm,
    cancel: () => { cancelled = true; clear(); },
  };
}

/**
 * Extend the session on the doctor's behalf — what «ادامهٔ کار» does.
 *
 * Returns true when the session is good again. On failure the caller shows the
 * expired dialog rather than retrying: if the refresh token is dead, it will
 * still be dead a second later, and a spinner that never resolves is a worse
 * answer than an honest one.
 */
export async function extendSession() {
  const ok = await refreshAccessToken().catch(() => false);
  return Boolean(ok);
}
